import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import CounterEvidenceUpload from './CounterEvidenceUpload';

const ResolutionDecisionPanel = ({ dispute, onSubmit }) => {
  const [responseType, setResponseType] = useState(null);
  const [responseMessage, setResponseMessage] = useState('');
  const [settlementAmount, setSettlementAmount] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [counterEvidence, setCounterEvidence] = useState([]);
  const [errors, setErrors] = useState({});

  const responseOptions = [
    {
      id: 'accept',
      label: 'Accept Dispute',
      description: 'Agree with the renter\'s claim and issue full refund',
      icon: 'CheckCircle',
      color: 'success'
    },
    {
      id: 'reject',
      label: 'Reject Dispute',
      description: 'Dispute the claim with counter-evidence',
      icon: 'XCircle',
      color: 'error'
    },
    {
      id: 'partial_settlement',
      label: 'Partial Settlement',
      description: 'Offer a compromise settlement amount',
      icon: 'DollarSign',
      color: 'warning'
    }
  ];

  const rejectionReasons = [
    'Evidence shows damage occurred during rental period',
    'Renter violated rental agreement terms',
    'Pre-rental inspection documentation contradicts claim',
    'Renter failed to report issue during rental period',
    'Charge is justified per rental policy',
    'Other (explain in message)'
  ];

  const validateForm = () => {
    const newErrors = {};

    if (!responseType) {
      newErrors.responseType = 'Please select a response type';
    }

    if (!responseMessage?.trim()) {
      newErrors.responseMessage = 'Please provide a detailed explanation';
    } else if (responseMessage?.trim()?.length < 50) {
      newErrors.responseMessage = 'Explanation must be at least 50 characters';
    }

    if (responseType === 'partial_settlement') {
      if (!settlementAmount || parseFloat(settlementAmount) <= 0) {
        newErrors.settlementAmount = 'Please enter a valid settlement amount';
      } else if (parseFloat(settlementAmount) >= dispute?.contestedAmount) {
        newErrors.settlementAmount = 'Settlement must be less than contested amount';
      }
    }

    if (responseType === 'reject' && !rejectionReason) {
      newErrors.rejectionReason = 'Please select a rejection reason';
    }

    if (responseType === 'reject' && counterEvidence?.length === 0) {
      newErrors.counterEvidence = 'Please upload counter-evidence to support your rejection';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = () => {
    if (!validateForm()) return;

    const responseData = {
      responseType,
      responseMessage,
      settlementAmount: responseType === 'partial_settlement' ? parseFloat(settlementAmount) : null,
      rejectionReason: responseType === 'reject' ? rejectionReason : null,
      counterEvidence,
      submittedAt: new Date()?.toISOString()
    };

    onSubmit(responseData);
  };

  return (
    <div className="bg-card rounded-lg border border-border p-6 space-y-6">
      <div>
        <h2 className="font-heading text-2xl font-bold text-foreground mb-2">
          Respond to Dispute
        </h2>
        <p className="font-body text-muted-foreground">
          Review the renter's claim and provide your response with supporting evidence
        </p>
      </div>

      {/* Response Type Selection */}
      <div>
        <label className="font-body font-semibold text-foreground mb-3 block">
          Select Your Response *
        </label>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {responseOptions?.map((option) => (
            <button
              key={option?.id}
              onClick={() => setResponseType(option?.id)}
              className={`p-4 rounded-lg border-2 transition-smooth text-left ${
                responseType === option?.id
                  ? `border-${option?.color} bg-${option?.color}/5`
                  : 'border-border hover:border-primary/50'
              }`}
            >
              <div className="flex items-start space-x-3">
                <Icon
                  name={option?.icon}
                  size={24}
                  className={responseType === option?.id ? `text-${option?.color}` : 'text-muted-foreground'}
                  strokeWidth={2}
                />
                <div>
                  <p className="font-body font-semibold text-foreground mb-1">{option?.label}</p>
                  <p className="font-caption text-xs text-muted-foreground">{option?.description}</p>
                </div>
              </div>
            </button>
          ))}
        </div>
        {errors?.responseType && (
          <p className="font-caption text-xs text-error mt-2">{errors?.responseType}</p>
        )}
      </div>

      {/* Partial Settlement Amount */}
      {responseType === 'partial_settlement' && (
        <div>
          <label className="font-body font-semibold text-foreground mb-2 block">
            Settlement Amount *
          </label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground font-body">
              $
            </span>
            <Input
              type="number"
              value={settlementAmount}
              onChange={(e) => setSettlementAmount(e?.target?.value)}
              placeholder="0.00"
              className="pl-8"
              min="0"
              max={dispute?.contestedAmount}
              step="0.01"
            />
          </div>
          <p className="font-caption text-xs text-muted-foreground mt-1">
            Original charge: ${dispute?.contestedAmount} • Enter amount you're willing to refund
          </p>
          {errors?.settlementAmount && (
            <p className="font-caption text-xs text-error mt-2">{errors?.settlementAmount}</p>
          )}
        </div>
      )}

      {/* Rejection Reason */}
      {responseType === 'reject' && (
        <div>
          <label className="font-body font-semibold text-foreground mb-2 block">
            Rejection Reason *
          </label>
          <select
            value={rejectionReason}
            onChange={(e) => setRejectionReason(e?.target?.value)}
            className="w-full px-4 py-2 rounded-md border border-border bg-background text-foreground font-body focus:outline-none focus:ring-2 focus:ring-primary"
          >
            <option value="">Select a reason...</option>
            {rejectionReasons?.map((reason, index) => (
              <option key={index} value={reason}>{reason}</option>
            ))}
          </select>
          {errors?.rejectionReason && (
            <p className="font-caption text-xs text-error mt-2">{errors?.rejectionReason}</p>
          )}
        </div>
      )}

      {/* Response Message */}
      <div>
        <label className="font-body font-semibold text-foreground mb-2 block">
          Detailed Explanation *
        </label>
        <textarea
          value={responseMessage}
          onChange={(e) => setResponseMessage(e?.target?.value)}
          placeholder="Provide a detailed explanation of your response. Include specific facts, dates, and references to evidence..."
          rows={6}
          className="w-full px-4 py-3 rounded-md border border-border bg-background text-foreground font-body focus:outline-none focus:ring-2 focus:ring-primary resize-none"
        />
        <div className="flex items-center justify-between mt-1">
          <p className="font-caption text-xs text-muted-foreground">
            Minimum 50 characters required
          </p>
          <p className={`font-caption text-xs ${
            responseMessage?.length >= 50 ? 'text-success' : 'text-muted-foreground'
          }`}>
            {responseMessage?.length} characters
          </p>
        </div>
        {errors?.responseMessage && (
          <p className="font-caption text-xs text-error mt-2">{errors?.responseMessage}</p>
        )}
      </div>

      {/* Counter-Evidence Upload */}
      {(responseType === 'reject' || responseType === 'partial_settlement') && (
        <div>
          <label className="font-body font-semibold text-foreground mb-2 block">
            Counter-Evidence {responseType === 'reject' && '*'}
          </label>
          <CounterEvidenceUpload evidence={counterEvidence} onChange={setCounterEvidence} />
          {errors?.counterEvidence && (
            <p className="font-caption text-xs text-error mt-2">{errors?.counterEvidence}</p>
          )}
        </div>
      )}

      {/* Response Templates */}
      <div className="bg-muted/30 rounded-lg p-4">
        <h4 className="font-body font-semibold text-foreground mb-2 flex items-center space-x-2">
          <Icon name="Lightbulb" size={18} strokeWidth={2} />
          <span>Response Tips</span>
        </h4>
        <ul className="space-y-2 font-body text-sm text-muted-foreground">
          <li className="flex items-start space-x-2">
            <Icon name="Check" size={16} className="text-success flex-shrink-0 mt-0.5" strokeWidth={2} />
            <span>Be professional and factual in your explanation</span>
          </li>
          <li className="flex items-start space-x-2">
            <Icon name="Check" size={16} className="text-success flex-shrink-0 mt-0.5" strokeWidth={2} />
            <span>Reference specific evidence and documentation</span>
          </li>
          <li className="flex items-start space-x-2">
            <Icon name="Check" size={16} className="text-success flex-shrink-0 mt-0.5" strokeWidth={2} />
            <span>Include timestamps and specific details</span>
          </li>
          <li className="flex items-start space-x-2">
            <Icon name="Check" size={16} className="text-success flex-shrink-0 mt-0.5" strokeWidth={2} />
            <span>Upload clear photos or documents to support your position</span>
          </li>
        </ul>
      </div>

      {/* Action Buttons */}
      <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-border">
        <Button
          variant="outline"
          size="lg"
          onClick={() => {
            setResponseType(null);
            setResponseMessage('');
            setSettlementAmount('');
            setRejectionReason('');
            setCounterEvidence([]);
            setErrors({});
          }}
          className="flex-1"
        >
          Reset Form
        </Button>
        <Button
          variant="default"
          size="lg"
          iconName="Send"
          onClick={handleSubmit}
          className="flex-1"
        >
          Submit Response
        </Button>
      </div>
    </div>
  );
};

export default ResolutionDecisionPanel;